import {
  createContext, useContext, useRef, useState, useCallback, useEffect, ReactNode,
} from 'react';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { getIceServers, logCall, logCallEvent } from '../api/calls';
import { getConversation, addMembers } from '../api/chat';
import { MeshCallSession } from '../webrtc/MeshCallSession';
import { startRingtone, startRingback, stopAllCallSounds, playConnectTone, playEndTone } from '../utils/call/callSounds';
import { setCallActive, isOtherCallActive } from '../utils/call/callBusy';
import { SpeakingMonitor } from '../utils/call/speaking';
import GroupCallScreen from '../components/call/GroupCallScreen';
import StartCallPicker from '../components/calls/StartCallPicker';

export type GroupCallStatus = 'idle' | 'incoming' | 'connecting' | 'connected' | 'ended';

export interface GroupParticipant {
  userId: string;
  name: string | null;
  avatarUrl: string | null;
  connState: RTCPeerConnectionState | 'new';
  /** True while this participant is actively talking (drives the avatar wave). */
  speaking?: boolean;
}

export interface GroupCallState {
  status: GroupCallStatus;
  callId: string | null;
  conversationId: string | null;
  conversationName: string | null;
  starterName: string | null;
  participants: GroupParticipant[];
  muted: boolean;
  startedAt: number | null;
}

const IDLE: GroupCallState = {
  status: 'idle', callId: null, conversationId: null, conversationName: null,
  starterName: null, participants: [], muted: false, startedAt: null,
};

const RING_TIMEOUT_MS = 45_000;
type SendFn = (frame: Record<string, any>) => void;

// Frames buffered while status === 'incoming' (session not yet created).
type BufferedFrame = { type: string; from: string; sdp?: RTCSessionDescriptionInit; candidate?: RTCIceCandidateInit };

interface GroupCallApi {
  groupCall: GroupCallState;
  startGroupCall: (conversationId: string) => Promise<void>;
  acceptGroupCall: () => Promise<void>;
  declineGroupCall: () => void;
  leaveGroupCall: () => void;
  toggleMute: () => void;
  addParticipants: (userIds: string[]) => Promise<void>;
  handleSignal: (frame: any) => void;
  bindSend: (fn: SendFn) => void;
}

const Ctx = createContext<GroupCallApi | null>(null);

export function GroupCallProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [groupCall, setGroupCall] = useState<GroupCallState>(IDLE);
  const [addingOpen, setAddingOpen] = useState(false);
  const sessionRef = useRef<MeshCallSession | null>(null);
  const sendRef = useRef<SendFn>(() => {});
  const routeRef = useRef<{ callId: string; conversationId: string } | null>(null);
  const audioSinks = useRef<Map<string, HTMLAudioElement>>(new Map());
  const audioHostRef = useRef<HTMLDivElement | null>(null);
  // Detects which remote participant is talking → drives the speaking wave.
  const speakingRef = useRef<SpeakingMonitor | null>(null);
  // Display info for members, resolved from the conversation.
  const membersRef = useRef<Record<string, { name: string | null; avatarUrl: string | null }>>({});
  // Frames received while incoming (session null) — replayed on acceptGroupCall.
  const pendingFrames = useRef<BufferedFrame[]>([]);
  // Synchronous fence so a double-tap on Accept can't create a second session.
  const acceptingRef = useRef(false);
  const isInitiatorRef = useRef(false);
  const startedAtRef = useRef<number | null>(null);
  const connectedEverRef = useRef(false);
  const eventLoggedRef = useRef(false);
  const convTypeRef = useRef<string | null>(null);

  // FIX 5: selfId from live useAuth() rather than a stale localStorage snapshot.
  const selfId = user?.id ?? '';

  const bindSend = useCallback((fn: SendFn) => { sendRef.current = fn; }, []);

  const sendTo = useCallback((toUserId: string, frame: Record<string, any>) => {
    const r = routeRef.current;
    if (!r) return;
    sendRef.current({ ...frame, call_id: r.callId, conversation_id: r.conversationId, to_user_id: toUserId });
  }, []);

  const fanLifecycle = useCallback((type: string) => {
    const r = routeRef.current;
    if (!r) return;
    sendRef.current({ type, call_id: r.callId, conversation_id: r.conversationId });
  }, []);

  const upsertParticipant = useCallback((userId: string, patch: Partial<GroupParticipant>) => {
    setGroupCall((c) => {
      const meta = membersRef.current[userId] || { name: null, avatarUrl: null };
      const existing = c.participants.find((p) => p.userId === userId);
      const next: GroupParticipant = existing
        ? { ...existing, ...patch }
        : { userId, name: meta.name, avatarUrl: meta.avatarUrl, connState: 'new', ...patch };
      const others = c.participants.filter((p) => p.userId !== userId);
      return { ...c, participants: [...others, next] };
    });
  }, []);

  const attachStream = useCallback((userId: string, stream: MediaStream) => {
    let el = audioSinks.current.get(userId);
    if (!el) {
      el = document.createElement('audio');
      el.autoplay = true;
      audioHostRef.current?.appendChild(el);
      audioSinks.current.set(userId, el);
    }
    el.srcObject = stream;
    void el.play().catch(() => {});
    // Watch this stream's loudness so the speaker's tile gets a "wave".
    if (!speakingRef.current) {
      speakingRef.current = new SpeakingMonitor((uid, speaking) => upsertParticipant(uid, { speaking }));
    }
    speakingRef.current.add(userId, stream);
  }, [upsertParticipant]);

  const detachStream = useCallback((userId: string) => {
    speakingRef.current?.remove(userId);
    const el = audioSinks.current.get(userId);
    if (el) { el.srcObject = null; el.remove(); audioSinks.current.delete(userId); }
  }, []);

  // FIX 3: forward-declare cleanup so useEffect unmount below can reference it.
  const cleanup = useCallback((ended: boolean) => {
    if (ended && !eventLoggedRef.current && routeRef.current) {
      eventLoggedRef.current = true;
      const r = routeRef.current;
      const startedAt = startedAtRef.current ?? Date.now();
      const endedAt = Date.now();
      const outcome = connectedEverRef.current ? 'completed' : 'no_answer';
      const durationS = connectedEverRef.current ? Math.max(0, Math.round((endedAt - startedAt) / 1000)) : 0;
      if (convTypeRef.current === 'call') {
        // Ad-hoc call session → minimal Calls-tab log (no chat thread). Each
        // participant logs its own owner row; null peer ⇒ "Group call".
        void logCall({
          call_id: r.callId, peer_user_id: null, conversation_id: r.conversationId,
          direction: isInitiatorRef.current ? 'outgoing' : 'incoming',
          call_type: 'voice', outcome, duration_s: durationS,
          started_at: new Date(startedAt).toISOString(), ended_at: new Date(endedAt).toISOString(),
        }).catch(() => {});
      } else if (isInitiatorRef.current) {
        // Existing group chat → in-thread call_event (Phase 3.6 behavior).
        void logCallEvent({
          call_id: r.callId, conversation_id: r.conversationId, call_type: 'voice',
          outcome, is_group: true, duration_s: durationS,
          started_at: new Date(startedAt).toISOString(), ended_at: new Date(endedAt).toISOString(),
        }).catch(() => {});
      }
    }
    stopAllCallSounds();
    speakingRef.current?.close();
    speakingRef.current = null;
    sessionRef.current?.closeAll();
    sessionRef.current = null;
    audioSinks.current.forEach((el) => { el.srcObject = null; el.remove(); });
    audioSinks.current.clear();
    routeRef.current = null;
    pendingFrames.current = [];
    acceptingRef.current = false;
    setAddingOpen(false);   // never let an open "Add" sheet leak into the next call
    setCallActive('group', false);
    setGroupCall(ended ? { ...IDLE, status: 'ended' } : IDLE);
    if (ended) setTimeout(() => setGroupCall(IDLE), 1500);
  }, []);

  const makeSession = useCallback((conversationId: string) => {
    const iceP = getIceServers();
    return iceP.then((iceServers) => {
      const session = new MeshCallSession(selfId, iceServers, {
        onSignal: (to, frame) => sendTo(to, frame),
        onParticipantAdded: (u) => upsertParticipant(u, { connState: 'new' }),
        onParticipantStream: (u, s) => attachStream(u, s),
        onParticipantStateChange: (u, st) => { if (st === 'connected') connectedEverRef.current = true; upsertParticipant(u, { connState: st }); },
        // FIX 2: auto-end logic moved outside the updater to keep it pure.
        onParticipantRemoved: (u) => {
          detachStream(u);
          setGroupCall((c) => ({ ...c, participants: c.participants.filter((p) => p.userId !== u) }));
          // After the state is scheduled, check peers synchronously via the session.
          // removePeer already called peers.delete before firing this callback,
          // so participantIds reflects the updated count.
          if (session.participantIds.length === 0) {
            // Last peer gone → end locally; tell anyone still there we left
            // (fan before cleanup clears routeRef).
            fanLifecycle('gcall-leave');
            cleanup(true);
          }
        },
      });
      sessionRef.current = session;
      return session;
    });
  }, [selfId, sendTo, upsertParticipant, attachStream, detachStream, cleanup, fanLifecycle]);

  const loadMembers = useCallback(async (conversationId: string) => {
    try {
      const conv: any = await getConversation(conversationId);
      const map: Record<string, { name: string | null; avatarUrl: string | null }> = {};
      for (const m of conv?.members || []) {
        map[m.user_id] = { name: m.name ?? m.display_name ?? null, avatarUrl: m.avatar_url ?? null };
      }
      membersRef.current = map;
      convTypeRef.current = conv?.type ?? null;
      const convName = conv?.name ?? (conv?.type === 'dm' ? null : 'Group call');
      setGroupCall((c) => ({ ...c, conversationName: convName }));
    } catch { /* best-effort display data */ }
  }, []);

  const startGroupCall = useCallback(async (conversationId: string) => {
    // FIX 1: use routeRef.current as a synchronous fence to prevent re-entrancy.
    // React state (groupCall.status) is stale across synchronous frames; routeRef
    // is set atomically in the same synchronous tick before any await.
    if (routeRef.current || isOtherCallActive('group')) return;
    const callId = crypto.randomUUID();
    // Set the synchronous fence and mark busy before any await.
    routeRef.current = { callId, conversationId };
    isInitiatorRef.current = true;
    startedAtRef.current = Date.now();
    connectedEverRef.current = false;
    eventLoggedRef.current = false;
    setCallActive('group', true);
    setGroupCall({ ...IDLE, status: 'connected', callId, conversationId, startedAt: Date.now() });
    void loadMembers(conversationId);
    try {
      const session = await makeSession(conversationId);
      await session.startLocalAudio();
      fanLifecycle('gcall-start');
    } catch {
      cleanup(false);
    }
  }, [makeSession, loadMembers, fanLifecycle, cleanup]);

  const acceptGroupCall = useCallback(async () => {
    // Re-entrancy guards (status is stale across synchronous frames): no route =
    // nothing to accept; an existing session or an in-flight accept means we
    // already answered — a double-tap must not spawn a second session.
    if (!routeRef.current || sessionRef.current || acceptingRef.current) return;
    acceptingRef.current = true;
    stopAllCallSounds();
    setGroupCall((c) => ({ ...c, status: 'connecting' }));
    try {
      const session = await makeSession(routeRef.current.conversationId);
      await session.startLocalAudio();
      setGroupCall((c) => ({ ...c, status: 'connected', startedAt: Date.now() }));
      fanLifecycle('gcall-join');
      // FIX 4: replay frames that arrived while status was 'incoming' (session was null).
      const buffered = pendingFrames.current;
      pendingFrames.current = [];
      for (const f of buffered) {
        if (f.type === 'gcall-join') void session.onJoin(f.from);
        else if (f.type === 'gcall-offer' && f.sdp != null) void session.handleOffer(f.from, f.sdp);
        else if (f.type === 'gcall-ice' && f.candidate != null) void session.handleIce(f.from, f.candidate);
      }
    } catch {
      cleanup(false);
    }
  }, [makeSession, fanLifecycle, cleanup]);

  const declineGroupCall = useCallback(() => {
    if (groupCall.status !== 'incoming') return;
    cleanup(false);
  }, [groupCall.status, cleanup]);

  const leaveGroupCall = useCallback(() => {
    if (groupCall.status === 'idle' || groupCall.status === 'ended') return;
    fanLifecycle('gcall-leave');
    cleanup(true);
  }, [groupCall.status, fanLifecycle, cleanup]);

  const toggleMute = useCallback(() => {
    setGroupCall((c) => {
      const muted = !c.muted;
      sessionRef.current?.setMuted(muted);
      return { ...c, muted };
    });
  }, []);

  const addParticipants = useCallback(async (userIds: string[]) => {
    const r = routeRef.current;
    if (!r || !userIds.length) return;
    try {
      await addMembers(r.conversationId, userIds);   // make them members (any member may add)
      await loadMembers(r.conversationId);            // refresh display names/avatars
      for (const uid of userIds) {
        sendTo(uid, { type: 'gcall-ring' });          // targeted invite into the live mesh
      }
    } catch {
      // Surface failures instead of swallowing them — otherwise a rejected
      // add just silently "does nothing", which reads as a participant cap.
      toast.error('Could not add to the call');
    }
  }, [loadMembers, sendTo]);

  // Ring/ringback/connect/end tones, driven off status (same pattern as 1:1).
  useEffect(() => {
    switch (groupCall.status) {
      case 'incoming': startRingtone(); break;
      case 'connecting': startRingback(); break;
      case 'connected': stopAllCallSounds(); playConnectTone(); break;
      case 'ended': stopAllCallSounds(); playEndTone(); break;
      default: stopAllCallSounds(); break;
    }
  }, [groupCall.status]);

  // FIX 3: unmount safety-net — stop any looping ringtone if the provider
  // is unmounted mid-ring (e.g. navigation replacing the root subtree).
  useEffect(() => () => stopAllCallSounds(), []);

  // Auto-dismiss an unanswered incoming ring.
  useEffect(() => {
    if (groupCall.status !== 'incoming') return;
    const id = setTimeout(() => cleanup(false), RING_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [groupCall.status, cleanup]);

  // Best-effort leave on tab close.
  useEffect(() => {
    const onUnload = () => {
      if (routeRef.current && (groupCall.status === 'connected' || groupCall.status === 'connecting')) {
        fanLifecycle('gcall-leave');
      }
    };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onUnload);
    };
  }, [groupCall.status, fanLifecycle]);

  const handleSignal = useCallback((frame: any) => {
    const t = frame?.type;
    if (t === 'gcall-start' || t === 'gcall-ring') {
      if (routeRef.current || isOtherCallActive('group')) {
        return; // busy — ignore (no auto-busy frame in group calls)
      }
      routeRef.current = { callId: frame.call_id, conversationId: frame.conversation_id };
      isInitiatorRef.current = false;
      startedAtRef.current = Date.now();
      eventLoggedRef.current = false;
      setCallActive('group', true);
      void loadMembers(frame.conversation_id);
      setGroupCall({
        ...IDLE, status: 'incoming', callId: frame.call_id, conversationId: frame.conversation_id,
        starterName: frame.from_name ?? frame.from_user_id,
      });
      return;
    }
    // Frames for the active call only.
    if (!routeRef.current || frame.call_id !== routeRef.current.callId) return;
    const session = sessionRef.current;
    const from = frame.from_user_id;
    // FIX 4: buffer join/offer/ice frames that arrive while session is null (status === 'incoming').
    if (!session) {
      if (t === 'gcall-join' || t === 'gcall-offer' || t === 'gcall-ice') {
        pendingFrames.current.push({ type: t, from, sdp: frame.sdp, candidate: frame.candidate });
      }
      return;
    }
    if (t === 'gcall-join') void session.onJoin(from);
    else if (t === 'gcall-present') void session.onPresence(from);
    else if (t === 'gcall-offer') void session.handleOffer(from, frame.sdp);
    else if (t === 'gcall-answer') void session.handleAnswer(from, frame.sdp);
    else if (t === 'gcall-ice') void session.handleIce(from, frame.candidate);
    else if (t === 'gcall-leave') session.removePeer(from);
  }, [loadMembers]);

  const apiValue: GroupCallApi = {
    groupCall, startGroupCall, acceptGroupCall, declineGroupCall, leaveGroupCall, toggleMute, addParticipants, handleSignal, bindSend,
  };

  return (
    <Ctx.Provider value={apiValue}>
      {children}
      {groupCall.status !== 'idle' && (
        <GroupCallScreen
          state={groupCall}
          onAccept={acceptGroupCall}
          onDecline={declineGroupCall}
          onLeave={leaveGroupCall}
          onToggleMute={toggleMute}
          onAddParticipant={groupCall.status === 'connected' ? () => setAddingOpen(true) : undefined}
        />
      )}
      {addingOpen && groupCall.status === 'connected' && (
        <StartCallPicker
          multi
          title="Add to call"
          excludeUserIds={[selfId, ...groupCall.participants.map(p => p.userId)]}
          onClose={() => setAddingOpen(false)}
          onPickMany={(users) => { setAddingOpen(false); void addParticipants(users.map(u => u.user_id)); }}
        />
      )}
      {/* Hidden host for one <audio> sink per participant. */}
      <div ref={audioHostRef} style={{ display: 'none' }} />
    </Ctx.Provider>
  );
}

export function useGroupCall(): GroupCallApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useGroupCall must be used within a GroupCallProvider');
  return ctx;
}
